/**
 * Report export utilities — CSV + PDF.
 *
 * Pure client-side. No backend changes required.
 *
 * Requires (one-time install):
 *   npm install jspdf jspdf-autotable
 *
 * Usage:
 *   exportCsv({ title: "Q1 P&L", headers: ["Account", "Amount"], rows: [["Sales", "100,000"], ...] })
 *   exportPdf({ title: "Q1 P&L", subtitle: "Acme Group · Jan 1 – Mar 31 2026", sections: [...] })
 */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ─── CSV ──────────────────────────────────────────────────────────

export interface CsvOptions {
  /** Report title (used in the filename, with spaces → underscores). */
  title: string;
  /** Optional subtitle line prepended as a comment. */
  subtitle?: string;
  /** Column headers (one row). */
  headers: string[];
  /** Data rows. Strings, numbers, null — all coerced to strings. */
  rows: Array<Array<string | number | null | undefined>>;
}

/**
 * Trigger a CSV download. UTF-8 BOM is prepended so Excel opens it
 * correctly with non-ASCII characters (e.g. GH₵, café, naira ₦).
 */
export function exportCsv({ title, subtitle, headers, rows }: CsvOptions) {
  const escape = (v: string | number | null | undefined): string => {
    const s = v == null ? "" : String(v);
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const lines: string[] = [];
  if (subtitle) {
    lines.push(`# ${subtitle}`);
    lines.push("");
  }
  lines.push(headers.map(escape).join(","));
  rows.forEach((row) => lines.push(row.map(escape).join(",")));

  const csv = "\uFEFF" + lines.join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  triggerDownload(blob, `${slug(title)}.csv`);
}

// ─── PDF ──────────────────────────────────────────────────────────

export interface PdfSection {
  /** Optional heading above the table. */
  heading?: string;
  /** Optional pre-table prose paragraph. */
  paragraph?: string;
  headers?: string[];
  rows?: Array<Array<string | number>>;
  /** Optional emphasized "footer" row with bold styling. */
  totalRow?: { label: string; value: string };
}

export interface PdfOptions {
  title: string;
  subtitle?: string;
  /** Right-aligned meta line (e.g. "Generated 5 Jun 2026"). */
  meta?: string;
  sections: PdfSection[];
}

/**
 * Trigger a PDF download. Uses Eaze brand:
 *   - Navy headers (#0A2540)
 *   - Eaze green accent rule (#4CAF50)
 *   - Helvetica throughout
 */
export function exportPdf({ title, subtitle, meta, sections }: PdfOptions) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const marginX = 14;

  // ─ Title block ─
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(10, 37, 64); // #0A2540
  doc.text(title, marginX, 22);

  if (subtitle) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100);
    doc.text(subtitle, marginX, 29);
  }

  if (meta) {
    doc.setFontSize(9);
    doc.setTextColor(140);
    doc.text(meta, pageW - marginX, 22, { align: "right" });
  }

  // Green accent rule
  doc.setFillColor(76, 175, 80); // #4CAF50
  doc.rect(marginX, 33, 30, 1.2, "F");

  let cursorY = 42;

  // ─ Sections ─
  sections.forEach((section, idx) => {
    if (idx > 0) cursorY += 4;

    if (section.heading) {
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(10, 37, 64);
      doc.text(section.heading, marginX, cursorY);
      cursorY += 6;
    }

    if (section.paragraph) {
      doc.setFontSize(9.5);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(60);
      const wrapped = doc.splitTextToSize(
        section.paragraph,
        pageW - marginX * 2,
      );
      doc.text(wrapped, marginX, cursorY);
      cursorY += wrapped.length * 4.5 + 2;
    }

    if (section.headers && section.rows) {
      autoTable(doc, {
        head: [section.headers],
        body: section.rows.map((r) => r.map(String)),
        startY: cursorY,
        theme: "grid",
        headStyles: {
          fillColor: [10, 37, 64],
          textColor: 255,
          fontSize: 9.5,
          fontStyle: "bold",
        },
        bodyStyles: {
          fontSize: 9.5,
          textColor: 30,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: marginX, right: marginX },
        foot: section.totalRow
          ? [[section.totalRow.label, section.totalRow.value]]
          : undefined,
        footStyles: section.totalRow
          ? {
              fillColor: [240, 244, 248],
              textColor: 10,
              fontStyle: "bold",
              fontSize: 10,
            }
          : undefined,
      });

      cursorY =
        (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable
          ?.finalY ?? cursorY + 40;
      cursorY += 4;
    }
  });

  // ─ Footer on every page ─
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(140);
    doc.text("Generated by Eaze · aeTech Digital Hub", marginX, 288);
    doc.text(`Page ${i} of ${totalPages}`, pageW - marginX, 288, {
      align: "right",
    });
  }

  doc.save(`${slug(title)}.pdf`);
}

// ─── Helpers ──────────────────────────────────────────────────────

function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}
